import React from 'react';
import {
    View,
    Text,
    StyleSheet,
    Modal,
    TouchableOpacity,
    TouchableWithoutFeedback,
    ActivityIndicator,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';

export type ModalType = 'success' | 'error' | 'confirm' | 'info';

export interface AppModalProps {
    visible: boolean;
    type: ModalType;
    title: string;
    message: string;
    onClose: () => void;
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
    loading?: boolean;
    customButtons?: React.ReactNode;
}

const modalConfig: Record<ModalType, { icon: string; color: string; bgColor: string }> = {
    success: {
        icon: 'check-circle',
        color: theme.colors.success,
        bgColor: '#E8F5E9',
    },
    error: {
        icon: 'alert-circle',
        color: theme.colors.error,
        bgColor: '#FFEBEE',
    },
    confirm: {
        icon: 'help-circle',
        color: theme.colors.warning,
        bgColor: '#FFF3E0',
    },
    info: {
        icon: 'information',
        color: theme.colors.info,
        bgColor: '#E3F2FD',
    },
};

export const AppModal: React.FC<AppModalProps> = ({
    visible,
    type,
    title,
    message,
    onClose,
    onConfirm,
    confirmText,
    cancelText = 'Cancel',
    loading = false,
    customButtons,
}) => {
    const config = modalConfig[type];
    const isConfirmModal = type === 'confirm';
    const defaultConfirmText = isConfirmModal ? 'Delete' : 'OK';

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
        } else {
            onClose();
        }
    };

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
            onRequestClose={onClose}
        >
            <TouchableWithoutFeedback onPress={isConfirmModal ? undefined : onClose}>
                <View style={styles.overlay}>
                    <TouchableWithoutFeedback>
                        <View style={styles.modalContainer}>
                            {/* Icon */}
                            <View style={[styles.iconContainer, { backgroundColor: config.bgColor }]}>
                                <Icon name={config.icon} size={48} color={config.color} />
                            </View>

                            {/* Title */}
                            <Text style={styles.title}>{title}</Text>

                            {/* Message */}
                            <Text style={styles.message}>{message}</Text>

                            {/* Actions */}
                            {customButtons ? (
                                customButtons
                            ) : (
                                <View style={styles.actions}>
                                    {isConfirmModal && (
                                        <TouchableOpacity
                                            style={styles.cancelButton}
                                            onPress={onClose}
                                            activeOpacity={0.7}
                                            disabled={loading}
                                        >
                                            <Text style={styles.cancelButtonText}>{cancelText}</Text>
                                        </TouchableOpacity>
                                    )}
                                    <TouchableOpacity
                                        style={[
                                            styles.confirmButton,
                                            { backgroundColor: isConfirmModal ? theme.colors.error : config.color },
                                            !isConfirmModal && styles.confirmButtonFull,
                                            loading && styles.buttonDisabled,
                                        ]}
                                        onPress={handleConfirm}
                                        activeOpacity={0.7}
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <ActivityIndicator size="small" color="#FFFFFF" />
                                        ) : (
                                            <Text style={styles.confirmButtonText}>
                                                {confirmText || defaultConfirmText}
                                            </Text>
                                        )}
                                    </TouchableOpacity>
                                </View>
                            )}
                        </View>
                    </TouchableWithoutFeedback>
                </View>
            </TouchableWithoutFeedback>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing.lg,
    },
    modalContainer: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.lg,
        width: '100%',
        maxWidth: 340,
        alignItems: 'center',
        ...theme.shadows.lg,
    },
    iconContainer: {
        width: 80,
        height: 80,
        borderRadius: 40,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
    },
    title: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
        textAlign: 'center',
        marginBottom: theme.spacing.sm,
    },
    message: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        marginBottom: theme.spacing.lg,
        lineHeight: 22,
    },
    actions: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        width: '100%',
    },
    cancelButton: {
        flex: 1,
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cancelButtonText: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    confirmButton: {
        flex: 1,
        paddingVertical: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        alignItems: 'center',
        justifyContent: 'center',
    },
    confirmButtonFull: {
        flex: 1,
    },
    confirmButtonText: {
        ...theme.typography.body1,
        color: '#FFFFFF',
        fontWeight: '600',
    },
    buttonDisabled: {
        opacity: 0.7,
    },
});
